import routes from 'common/constants/routes';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import { useAuthorization } from './use-authorization';

export interface MappedRoute {
  title: string;
  subNav?: MappedRoute[];
  itemId?: string;
  leftIcon?: (color: string, size: number) => React.ReactNode;
}

export default function useSearchMenu() {
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [searchNavigation, setSearchNavigation] = React.useState('');
  const { can } = useAuthorization();
  const { t } = useTranslation();

  const onFocusSearch = React.useCallback((event: KeyboardEvent) => {
    if (
      (event.ctrlKey || event.metaKey) &&
      event.shiftKey &&
      event.code === 'KeyF'
    ) {
      searchInputRef.current?.focus();
    }
    return false;
  }, []);

  const MapAllRoute: (item, searchValue: string) => MappedRoute | undefined =
    React.useCallback(
      (item, searchValue) => {
        if (item.children) {
          return {
            title: t(item.title),
            subNav: item?.children
              ?.map((child) => MapAllRoute(child, searchValue))
              .filter((x) => x !== undefined),
            leftIcon: item?.leftIcon,
          } as MappedRoute;
        }

        if (!item.permission || !can(item.permission) || item.hide) {
          return undefined;
        }

        const navigation = {
          title: t(item.title),
          itemId: item.routeName,
          leftIcon: item?.leftIcon,
        } as MappedRoute;

        if (searchValue) {
          const title = t(item.title);

          if (title?.toLowerCase().includes(searchValue?.toLowerCase())) {
            return navigation;
          }

          return undefined;
        }

        return navigation;
      },
      [can, t],
    );

  const FilterAllUnUsedNavigation = React.useCallback((item: MappedRoute[]) => {
    return item.filter((currentItem) => {
      if (!('subNav' in currentItem)) {
        return true;
      }

      if (currentItem?.subNav?.length) {
        const current = FilterAllUnUsedNavigation(currentItem?.subNav);

        currentItem.subNav = current;

        return !!current.length;
      } else {
        return false;
      }
    }) as MappedRoute[];
  }, []);

  const NAVIGATION = React.useMemo(() => {
    return routes
      .map((route) => MapAllRoute(route, searchNavigation))
      .filter((x) => x !== undefined) as MappedRoute[];
  }, [MapAllRoute, searchNavigation]);

  const FILTERED_NAVIGATION: MappedRoute[] = React.useMemo(() => {
    const routes = FilterAllUnUsedNavigation([...NAVIGATION]);
    if (NAVIGATION) {
      return routes;
    }

    return [];
  }, [FilterAllUnUsedNavigation, NAVIGATION]);

  return {
    searchInputRef,
    searchNavigation,
    setSearchNavigation,
    NAVIGATION,
    FILTERED_NAVIGATION,
    onFocusSearch,
  };
}
